import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {

  #abortController = null;
  #cachedContent = new Map();
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = this.closest('product-card');
    const productLink = productCard?.getProductCardLink();
    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    const selectedVariantId = productCard?.getSelectedVariantId();
    if (selectedVariantId) url.searchParams.set('variant', selectedVariantId);

    return url.toString();
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
  }

  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  handleClick = async (event) => {
    event.preventDefault();

    const url = this.productPageUrl;

    let productGrid = this.#cachedContent.get(url);

    if (!productGrid) {
      const html = await this.fetchProductPage(url);
      const gridElement = html?.querySelector('[data-product-grid-content]');
      if (gridElement) {
        productGrid = gridElement.cloneNode(true);
        this.#cachedContent.set(url, productGrid);
      }
    }

    if (productGrid) {
      const fresh = productGrid.cloneNode(true);
      await this.updateQuickAddModal(fresh);
    }

    this.#openQuickAddModal();
  };

async fetchProductPage(url) {
  if (!url) return null;

  this.#abortController?.abort();
  this.#abortController = new AbortController();

  try {
    const res = await fetch(url, {
      signal: this.#abortController.signal
    });

    const text = await res.text();

    return new DOMParser().parseFromString(text, 'text/html');

  } catch (error) {

    // ⭐ IMPORTANT — Ignore abort errors
    if (error.name === 'AbortError') {
      return null;
    }

    console.error(error);
    return null;

  } finally {
    this.#abortController = null;
  }
}


  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    morph(modalContent, productGrid);

    // 🔥 IMPORTANT — initialize membership system
    this.#initMembershipPricing(modalContent);
    this.#autoSelectMembership(modalContent);
  }

  // ⭐ CLICK HANDLER FOR MEMBER / REGULAR
  #initMembershipPricing(modalContent) {

    const box = modalContent.querySelector('.membershipbox');
    if (!box) return;

    box.addEventListener('click', (e) => {

      const option = e.target.closest('.price-option');
      if (!option) return;

      // UI selection
      box.querySelectorAll('.price-option').forEach(el =>
        el.classList.remove('selected')
      );

      option.classList.add('selected');

      const radio = option.querySelector('input[type="radio"]');
      if (radio) radio.checked = true;

      // 🔥 SET REAL VARIANT ID
      const form = modalContent.querySelector('product-form-component form');
      const variantInput = form?.querySelector('input[name="id"]');

      const variantId = option.dataset.variantId;

      if (variantInput && variantId) {
        variantInput.value = variantId;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

    });
  }

  // ⭐ AUTO SELECT MEMBER / REGULAR BASED ON LOGIN
  #autoSelectMembership(modalContent) {

    const isMember = document.body.classList.contains("customer-member");

    const selector = isMember
      ? '.priceopt_member'
      : '.priceopt_regular';

    const option = modalContent.querySelector(selector);

    if (option) option.click();
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}


/* ================= DIALOG ================= */

class QuickAddDialog extends DialogComponent {

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      ThemeEvents.cartUpdate,
      this.handleCartUpdate,
      { signal: this.#abortController.signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}



/* ================= MEMBERSHIP POPUP js for quick poup ================= */

document.addEventListener('click', function (e) {

  const addBtn = e.target.closest(
    '.addto_normalbtn, .addto_specialbtn, .membership_popbtn, .hdt-product-form__submit'
  );

  if (!addBtn) {
    return;
  }

  const isMemberUser =
    document.body.classList.contains('customer-member');

  // ⭐ CHECK MEMBER PRICE SELECTED
  const memberSelected =
    document.querySelector('.priceopt_member.selected');

  // ⭐ MEMBER USER → allow add
  if (isMemberUser) {
    return;
  }

  // ⭐ REGULAR PRICE → allow add
  if (!memberSelected) {
    return;
  }

  // ⭐ NON-MEMBER + MEMBER PRICE → SHOW POPUP
  console.log("🔥 SHOW POPUP NOW");

  e.preventDefault();
  e.stopImmediatePropagation();

  const popup = document.querySelector('.membership-upsell-popup');

  if (popup) {
    popup.style.display = 'flex';
    document.body.classList.add('bodyfixforpop');
  } else {
    console.log("❌ Popup element not found");
  }

}, true);


/* ================= POPUP CLOSE ================= */

document.addEventListener('click', function (e) {

  if (e.target.classList.contains('upselpopupclose')) {
    const popup = document.querySelector('.membership-upsell-popup');
    if (popup) popup.style.display = 'none';
    document.body.classList.remove('bodyfixforpop');
  }

});
